/**
 * NFT Claim Component
 * A React component that enables users to claim NFTs by selecting a unique ID
 * and interacting with a smart contract through MetaMask
 */

import { ethers } from "ethers";
import { getWalletBrowserProvider } from "../../services/wallet-provider";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

import Ptdc from "../utility/ptdc";
import NotificationClaim from "../utility/notification-claim";

// eslint-disable-next-line no-undef
const serverUrl = process.env.REACT_APP_SERVER_URL;

/**
 * NFT Claim component
 * Provides interface for users to select and claim NFTs using unique IDs
 * @returns {JSX.Element} Rendered component
 */
const Claim = () => {
  /**
   * Component state
   */
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [nftMetadata, setNftMetadata] = useState(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const config = useSelector((state) => state.config);
  const { collection } = useParams();
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [isLegitClaim, setIsLegitClaim] = useState();
  const [isCheckingLegitimacy, setIsCheckingLegitimacy] = useState(true);
  const [isAlreadyClaimed, setIsAlreadyClaimed] = useState(false);

  /**
   * Smart contract interface definition
   */
  const contractABI = [
    "function claimNFT(string uniqueId) external",
    "function isIdValid(string uniqueId) external view returns (bool)",
    "function isIdClaimed(string uniqueId) external view returns (bool)",
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function totalSupply() view returns (uint256)",
    "function maxSupply() view returns (uint256)",
    "function remainingSupply() view returns (uint256)",
    "function owner() view returns (address)",
    "function tokenURI(uint256 tokenId) view returns (string)",
    "function _baseURI() view returns (string)",
    "function setBaseURI(string) external",
  ];

  /**
   * Effect hook to fetch NFT metadata
   */
  useEffect(() => {
    const fetchNFTMetadata = async () => {
      setIsLoadingMetadata(true);
      try {
        if (!collection) {
          return;
        }

        // Controllo più rigoroso su config e collections
        if (!config?.clientConfig?.data?.collections) {
          return;
        }

        const collectionAddress = collection;

        // if (
        //   typeof collectionAddress !== "string" ||
        //   !collectionAddress.trim()
        // ) {
        //   console.log("Invalid collection address");
        //   return;
        // }

        try {
          const response = await axios.get(
            `${serverUrl}/api/collection/address/${collectionAddress}`,
          );

          setNftMetadata(response.data);
        } catch (contractError) {
          setError(
            `Could not fetch contract information: ${contractError.message}`,
          );
        }
      } catch (error) {
        setError(`Connection error: ${error.message}`);
      } finally {
        setIsLoadingMetadata(false);
      }
    };

    fetchNFTMetadata();
  }, [collection, config]);

  /**
   * Handles the NFT claiming process
   */
  const claim = async () => {
    if (!claimId) {
      setError("There is no Claim ID");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const provider = await getWalletBrowserProvider();
      const signer = await provider.getSigner();

      const contractAddress = collection;
      const contract = new ethers.Contract(
        contractAddress,
        contractABI,
        signer,
      );

      const isValid = await contract.isIdValid(claimId);
      const isClaimed = await contract.isIdClaimed(claimId);

      if (!isValid) {
        throw new Error("Invalid ID");
      }

      if (isClaimed) {
        setIsAlreadyClaimed(true);
        throw new Error("This ID has already been claimed");
      }

      const tx = await contract.claimNFT(claimId);
      await tx.wait();
    } catch (error) {
      setError(error.message || "Error during claim");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    try {
      setIsCheckingLegitimacy(true);

      const fetchData = async () => {
        if (
          collection &&
          claimId &&
          config &&
          config?.clientConfig?.data.collections &&
          config?.clientConfig?.data.collections.includes(collection)
        ) {
          const legitClaimRequest = {
            collectionAddress: collection,
            claimId: claimId,
          };

          await axios.post(
            serverUrl + "/api/is-claim-id-legit",
            legitClaimRequest,
          );

          setIsLegitClaim(false);
        }
      };

      fetchData();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsCheckingLegitimacy(false);
    }
  }, [collection, claimId, config]);

  const reportClaim = () => {
    navigate(`/report/${collection}/${claimId}`);
  };

  const truncateDescription = (text, maxLength) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  return (
    <div className="claim-page container-cust mx-auto">
      {isLoadingMetadata ? (
        <div className="public-state public-state-loading mb-6 p-4 border rounded-lg">
          <p>Loading NFT metadata...</p>
        </div>
      ) : (
        nftMetadata && (
          <>
            <div className="notification-section">
              {!isCheckingLegitimacy &&
                (isLegitClaim ? (
                  <NotificationClaim error={error} legit={true} />
                ) : (
                  <NotificationClaim error={error} legit={false} />
                ))}
            </div>

            <div className="grid grid-cols-12 gap-4 claim-product-section">
              <div className="col-span-12 sm:col-span-10 sm:col-start-2 lg:col-span-6 lg:col-start-4">
                <div className="product-claim">
                  {nftMetadata.image && (
                    <div className="image-container">
                      <img src={nftMetadata.image} alt={nftMetadata.name} />
                    </div>
                  )}
                  <div className="text-container">
                    <Ptdc
                      title={nftMetadata.name}
                      description={truncateDescription(
                        nftMetadata.description,
                        150,
                      )}
                    />

                    {isAlreadyClaimed && (
                      <button
                        onClick={reportClaim}
                        className="cta cta-negative"
                      >
                        Apri ticket
                      </button>
                    )}
                    {!isAlreadyClaimed && (
                      <button
                        className="cta cta-negative"
                        onClick={claim}
                        disabled={isLoading}
                      >
                        {isLoading ? "Processing..." : "Start Claim"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default Claim;
